/**
 * useInfoModePreview — plays a one-shot color-scheme carousel on the first
 * entry to Info mode within the current page session.
 *
 * Behaviour:
 *  - Triggers once per page session (module-level flag).
 *  - Cycles through every available non-MONO color scheme at `intervalMs`
 *    cadence, then returns to MONO.
 *  - Skipped entirely when only MONO is available (nothing to cycle).
 *  - Aborted immediately if `userInteracted` becomes true (user touched the
 *    scheme selector or map before the preview finished).
 *  - If `force2DVariant` is provided (and `setViewerVariant`) the hook
 *    temporarily switches the viewer to 2D before cycling so the color
 *    mutations are visible, then restores `restoreVariant` when done.
 *    Use this when the renderer is in 3D but the user navigated there from
 *    another mode (not a configured Info+3D default start).
 *
 * @param {object}  params
 * @param {string[]}           params.availableColorSchemes  — ordered list from capabilities
 * @param {string}             params.monoScheme             — COLOR_SCHEME.MONO value
 * @param {boolean}            params.enabled                — gate (SVG mounted)
 * @param {boolean}            params.userInteracted         — flips true on first interaction
 * @param {(s: string) => void} params.setActiveColorScheme
 * @param {number}             [params.intervalMs=600]
 * @param {string|null}        [params.force2DVariant=null]  — viewer variant to switch to before cycling
 * @param {string|null}        [params.restoreVariant=null]  — viewer variant to restore after cycling
 * @param {((v: string) => void)|null} [params.setViewerVariant=null]
 */

import { useEffect, useRef } from 'react'

let previewPlayedThisSession = false

export function __clearInfoPreviewMemoryForTests() {
  previewPlayedThisSession = false
}

export function useInfoModePreview({
  availableColorSchemes,
  monoScheme,
  enabled,
  userInteracted,
  setActiveColorScheme,
  intervalMs = 600,
  force2DVariant = null,
  restoreVariant = null,
  setViewerVariant = null,
}) {
  const schemesRef             = useRef(availableColorSchemes)
  schemesRef.current           = availableColorSchemes
  const setSchemeRef           = useRef(setActiveColorScheme)
  setSchemeRef.current         = setActiveColorScheme
  const userInteractedRef      = useRef(userInteracted)
  userInteractedRef.current    = userInteracted
  const setViewerVariantRef    = useRef(setViewerVariant)
  setViewerVariantRef.current  = setViewerVariant

  useEffect(() => {
    if (!enabled) return
    if (userInteracted) return
    if (previewPlayedThisSession) return

    const nonMono = schemesRef.current.filter((s) => s !== monoScheme)
    if (nonMono.length === 0) {
      previewPlayedThisSession = true
      return
    }

    previewPlayedThisSession = true

    // Temporarily switch to 2D so color-scheme mutations are visible.
    // Captured at effect-run time so restore is correct even after re-renders.
    let didForce2D = false
    if (force2DVariant != null && setViewerVariantRef.current != null) {
      setViewerVariantRef.current(force2DVariant)
      didForce2D = true
    }

    // Steps: each non-MONO scheme, then back to MONO.
    const steps = [...nonMono, monoScheme]
    let cancelled = false
    let restored = false
    let timer = null

    const restore = () => {
      if (restored) return
      restored = true
      if (didForce2D && restoreVariant != null && setViewerVariantRef.current != null) {
        setViewerVariantRef.current(restoreVariant)
      }
    }

    const runStep = (i) => {
      if (cancelled || userInteractedRef.current) {
        restore()
        return
      }
      if (i >= steps.length) {
        restore()
        return
      }

      setSchemeRef.current(steps[i])
      timer = window.setTimeout(() => runStep(i + 1), intervalMs)
    }

    timer = window.setTimeout(() => runStep(0), intervalMs)

    return () => {
      cancelled = true
      if (timer != null) window.clearTimeout(timer)
      restore()
    }
  }, [enabled, userInteracted, monoScheme, intervalMs]) // eslint-disable-line react-hooks/exhaustive-deps -- force2DVariant/restoreVariant/setViewerVariant intentionally omitted: one-shot session preview, values captured at effect-run time via refs
}
